import type { FC } from "react";
import { useMemo, useState } from "react";

import { Dropdown, IconButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import managementTrackingService from "app/pages/sales-intelligence/modules/management/services/tracking/managementTrackingService";
import { resolveSignalsApiCustomAttributes } from "app/pages/sales-intelligence/modules/management/services/tracking/utils";
import { useSignalsApiStatusPollingQuery } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/queries/useSignalsApiStatusPollingQuery";
import { useFileUpload } from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/hooks/useFileUpload";
import { useUploadDomainsFile } from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/mutations/useUploadDomainsFile";
import {
    SubscribeDomainsUploadMode,
    SubscribeDomainsUploadModeId,
} from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/types/enum";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { DOMAINS_MANAGEMENT_SUBMIT_DD_ANCHOR, DROPDOWN_WIDTH } from "./constants";
import { StyledDDContainer, StyledDropdownItem } from "./styles";

interface DomainManagementButtonContainerProps {
    isDisabled: boolean;
}

export const DomainManagementButtonContainer: FC<DomainManagementButtonContainerProps> = ({ isDisabled }) => {
    const [selectedMode, setSelectedMode] = useState(SubscribeDomainsUploadMode.Append);
    const uploadFile = useUploadDomainsFile();

    const signalApiDomains = salesSettingsHelper.getSalesApiSignalsDomainsLimit() || 0;
    const { data } = useSignalsApiStatusPollingQuery();

    const { FileInput, openFileDialog } = useFileUpload({
        accept: ".csv",
        onFilesSelected: (files: File[]) => {
            uploadFile.mutate({ file: files[0], mode: selectedMode });
        },
    });

    const handleSelect = (item: { id: SubscribeDomainsUploadModeId }) => {
        const uploadMode =
            item.id === SubscribeDomainsUploadModeId.Replace
                ? SubscribeDomainsUploadMode.Replace
                : SubscribeDomainsUploadMode.Append;
        openFileDialog();
        setSelectedMode(uploadMode);
        managementTrackingService.trackSignalApiClickEvent(`${uploadMode}`, resolveSignalsApiCustomAttributes(data));
    };

    const dropdownItems = useMemo(() => {
        const replaceItemButton = (
            <StyledDropdownItem
                key="item-save-as-new"
                id="replace-domains-action"
            >
                {translate("si.management.integration.subscribe-domains.button.replace-domains")}
            </StyledDropdownItem>
        );
        return data?.subscribedDomainsCount && signalApiDomains <= data?.subscribedDomainsCount
            ? [replaceItemButton]
            : [
                  <StyledDropdownItem
                      key="item-update"
                      id="add-domains-action"
                  >
                      {translate("si.management.integration.subscribe-domains.button.add-domains")}
                  </StyledDropdownItem>,
                  replaceItemButton,
              ];
    }, [signalApiDomains, data?.subscribedDomainsCount]);

    return (
        <div>
            {FileInput}
            <StyledDDContainer id={DOMAINS_MANAGEMENT_SUBMIT_DD_ANCHOR}>
                <Dropdown
                    width={DROPDOWN_WIDTH}
                    onClick={handleSelect}
                    dropdownPopupPlacement="bottom-right"
                    appendTo={`#${DOMAINS_MANAGEMENT_SUBMIT_DD_ANCHOR}`}
                >
                    {[
                        <IconButton
                            type="flat"
                            iconSize={"sm"}
                            isDisabled={isDisabled}
                            iconName="refresh_list"
                            dataAutomation="domains-management-button"
                        >
                            {translate("si.management.integration.subscribe-domains.button.manage-domains")}
                        </IconButton>,
                        ...dropdownItems,
                    ]}
                </Dropdown>
            </StyledDDContainer>
        </div>
    );
};
